import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useStyles } from "./OrderDetails.styles";
import { ReactComponent as LeftArrowIcon } from "../../assets/leftArrow.svg";
import { ReactComponent as DeliveryTruckIcon } from "../../assets/deliveryTruck.svg";
import { ReactComponent as EmptyIcon } from "../../assets/empty.svg";

import {
  OrderState,
  ProcessedOrderDetails,
} from "../../pages/OrderDetailsPage/OrderDetailsTypes";
import OrderTimeLine from "../common/order-time-line/order-time-line.component";
import DeliveryCard from "./DeliveryCard/DeliveryCard";
import Loader from "../common/loader/loader.component";
import UnavailableDelivery from "./UnavailableDelivery/UnavailableDelivery";
import Summary from "./Summary/Summary";
import { getDateInfo } from "../../shared/dates";
import { useCopyOrderToBasket } from "./hooks/useCopyOrderToBasket";
import { Snackbar } from "@material-ui/core";
import CircleLoader from "../common/loader/circle-loader.component";
import AccountCancelOrder from "./CancelOrder/CancelOrder";

interface OrderDetailsProps extends ProcessedOrderDetails {}

const OrderDetails: React.FC<OrderDetailsProps> = ({
  loading,
  error,
  isFullyCancelled,
  cancelledAt,
  deliveries,
  unavailableItems,
  summary,
  raw,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { copyOrder, loading: copyLoading } = useCopyOrderToBasket();

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackMessage, setSnackMessage] = React.useState("");

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const dir = t("css.side") as "rtl" | "ltr";
  const classes = useStyles({ dir });

  const { state, code, id } = raw || {};
  const isDelivered = state?.toLowerCase() === OrderState.COMPLETED;

  const onGoBack = () => {
    history.push("/account/orders");
  };

  const formatCancelledData = (dateString: string | null) => {
    const dateInfo = getDateInfo(dateString);
    const customFormattedDate = dateInfo?.cancelledOn;
    return customFormattedDate;
  };

  const handleCopyOrder = async () => {
    // Collect all items from all deliveries
    const allLines = deliveries.flatMap((d) => d.items);

    const res = await copyOrder(allLines);

    if (!res.ok) {
      const err = res.failed;

      const translationKey = `errors.addItemToOrder.${err?.type}`;
      const translatedMessage = t(translationKey, t("errors.generic"));

      setSnackMessage(translatedMessage);
      setSnackOpen(true);
    }
  };

  if (loading) {
    return (
      <div className={classes.loaderContainer}>
        <Loader size={64} color={"#B73232"} />;
      </div>
    );
  }

  if (!raw || !code) {
    return (
      <div className={classes.noOrderFound}>
        <span>{t("account.noOrderFound")}.</span>
        <EmptyIcon />
      </div>
    );
  }

  return (
    <>
      <div className={classes.OrderDetailsContainer}>
        <div>
          <div className={classes.OrderDetailsGoBack}>
            <LeftArrowIcon />{" "}
            <span onClick={onGoBack}>{t("common.Go back")}</span>
          </div>
        </div>
        <div className={classes.orderDetailsIDContainer}>
          <h2 className={classes.OrderDetailsID}>
            {t("account.order")} #{code}
          </h2>
          {isDelivered || isFullyCancelled ? (
            <OrderTimeLine state={state} />
          ) : null}
        </div>
        <div className={classes.subContainer}>
          <div className={classes.deliveriesContainer}>
            {isFullyCancelled ? (
              <div className={classes.cancelledMsgCard}>
                <span className={classes.cancelledMsgHeader}>
                  {t("account.orderCanceledOn", {
                    date: formatCancelledData(cancelledAt),
                  })}
                </span>
                <span className={classes.cancelledMsg}>
                  {t("account.cancelledMessageHeader")}
                </span>
                <span className={classes.cancelledMsg}>
                  {t("account.cancelledMessageSubHeader")}
                </span>
              </div>
            ) : null}
            {deliveries?.map((delivery) => (
              <DeliveryCard
                key={delivery.subOrderId}
                index={delivery.index}
                total={deliveries?.length}
                state={delivery.state.toLowerCase() as any}
                deliveryDate={delivery.deliveryDate}
                timeWindow={delivery.timeWindow}
                items={delivery?.items}
              />
            ))}
            {unavailableItems?.length ? (
              <UnavailableDelivery items={unavailableItems} />
            ) : null}
            <Summary summary={summary} />
          </div>
          <div>
            <div className={classes.deliveryTo}>
              <div className={classes.deliveryToHeader}>
                <DeliveryTruckIcon className={classes.deliveryTruckIcon} />
                <span className={classes.deliveryToText}>
                  {t("account.deliveryTo")}
                </span>
              </div>
              <div className={classes.customerDetails}>
                <span>
                  {raw?.customer?.firstName} {raw?.customer?.lastName}
                </span>
                <span>
                  {raw?.shippingAddress?.streetLine1},{" "}
                  {raw?.shippingAddress?.city + ","}{" "}
                  {raw?.shippingAddress?.postalCode}{" "}
                  {raw?.shippingAddress?.country}
                </span>
                <span className={classes.phoneNumber}>
                  {raw?.customer?.phoneNumber}
                </span>
              </div>
            </div>
            <div className={classes.actionItems}>
              <button
                className={classes.copyOrderButton}
                disabled={copyLoading}
                onClick={handleCopyOrder}
              >
                {copyLoading ? (
                  <CircleLoader size={16} color="#FFF" />
                ) : (
                  t("account.copyOrderToBasket")
                )}
              </button>
              {!isFullyCancelled ? <AccountCancelOrder orderId={id!} /> : null}
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />
    </>
  );
};

export default OrderDetails;
